/**
 * Map hover-popup markup built from UI field metadata
 * (`/api/broker_deals/metadata?table_name=…` rows: field, label, ord, hide,
 * is_date, format, …). Labels, order and visibility come from the metadata;
 * nothing here names a field.
 */
import { formatDateOnly } from "./dateFormat";
import { fmtNumber, learnFieldFormats, titleCase } from "./metadataV2Columns";

/** Minimal HTML escape for values injected into popup markup. */
export const esc = (v) => {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
};

const isBlank = (v) => v === null || v === undefined || v === "";

const sortByOrd = (rows) =>
  [...rows].sort((a, b) => {
    const ao = Number.isFinite(Number(a?.ord)) ? Number(a.ord) : Infinity;
    const bo = Number.isFinite(Number(b?.ord)) ? Number(b.ord) : Infinity;
    return ao - bo;
  });

const defaultFormat = (meta, value) => {
  if (meta?.is_date || meta?.data_type === "Date") {
    return formatDateOnly(value) ?? String(value);
  }
  if (meta?.format || meta?.is_numeric) {
    learnFieldFormats([meta]);
    return fmtNumber(value, meta.field);
  }
  return String(value);
};

/**
 * Label/value pairs for a feature's properties, driven by metadata: ordered
 * by `ord`, `hide` rows skipped, and only fields that carry a value on this
 * feature. A row with no `label` falls back to the title-cased field name.
 *
 * @param {object} props  feature properties
 * @param {Array<object>} metadata
 * @param {object} [options]
 * @param {Record<string,(value:any)=>string>} [options.formatters]
 *   per-field value overrides
 * @param {string[]} [options.exclude]  fields to leave out (e.g. shown as title)
 * @returns {Array<{field:string,label:string,text:string}>}
 */
export const metadataPopupRows = (props, metadata, options = {}) => {
  const p = props || {};
  const formatters = options.formatters || {};
  const exclude = new Set(options.exclude || []);
  const rows = Array.isArray(metadata) ? metadata : [];
  const out = [];
  const seen = new Set();
  for (const meta of sortByOrd(rows)) {
    const field = meta?.field;
    if (!field || meta.hide || exclude.has(field) || seen.has(field)) continue;
    if (!Object.prototype.hasOwnProperty.call(p, field)) continue;
    const value = p[field];
    if (isBlank(value)) continue;
    seen.add(field);
    const fmt = formatters[field];
    out.push({
      field,
      label: meta.label || titleCase(field),
      text: fmt ? fmt(value) : defaultFormat(meta, value),
    });
  }
  return out;
};

/**
 * Popup HTML. Every interpolated string is escaped.
 *
 * @param {object} args
 * @param {string} [args.title]
 * @param {string} [args.swatch]  CSS color for a small square before the title
 * @param {Array<{label:string,text:string}>} args.rows
 * @param {string} [args.note]   muted line under the rows
 */
export const renderPopupHtml = ({ title, swatch, rows, note }) => {
  const sw = swatch
    ? `<span style="display:inline-block;width:10px;height:10px;margin-right:6px;` +
      `border:2px solid ${esc(swatch)};vertical-align:-1px"></span>`
    : "";
  const head = title
    ? `<div style="font-weight:600">${sw}${esc(title)}</div>`
    : "";
  const body = (rows || [])
    .map(
      (r) =>
        `<div><span style="color:#8c8c8c">${esc(r.label)}:</span> ${esc(r.text)}</div>`
    )
    .join("");
  const foot = note
    ? `<div style="margin-top:4px;color:#8c8c8c;font-style:italic">${esc(note)}</div>`
    : "";
  return `<div style="font-size:12px;line-height:16px;">${head}${body}${foot}</div>`;
};
